import React from 'react';
import { useAPR } from '../context/APRContext';
import { useAuth } from '../context/AuthContext';
import { APRRun, APRModel, Application } from '../types';
import apiService, { authFetch } from '../services/api';
import QAAssessmentModal, { AssessmentResults } from '../components/QAAssessmentModal';

// Interface for lifecycle status data
interface LifecycleStatus {
  apr_model_id: string;
  apr_model_name: string;
  model_status: string;
  total_applications: number;
  qa_assessment: {
    completed: number;
    in_progress: number;
    not_started: number;
    percentage: number;
  };
  final_disposition: {
    completed: number;
    pending: number;
    not_initialized: number;
    percentage: number;
  };
  integration_transfer: {
    transferred: number;
    retain_confirmed: number;
    pending: number;
    percentage: number;
  };
  in_development: {
    count: number;
    percentage: number;
  };
  disposed: {
    count: number;
    percentage: number;
  };
}

const APRExecution: React.FC = () => {
  const { state, dispatch, refreshData } = useAPR();
  const { hasPermission } = useAuth();
  const [showDetailsModal, setShowDetailsModal] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState<APRRun | APRModel | null>(null);
  const [executingId, setExecutingId] = React.useState<string | null>(null);
  const [modelApplications, setModelApplications] = React.useState<Record<string, Application[]>>({});
  const [loadingApplications, setLoadingApplications] = React.useState(false);
  const [showQAModal, setShowQAModal] = React.useState(false);
  const [selectedApplication, setSelectedApplication] = React.useState<{ id: string; name: string; aprRunId?: string } | null>(null);
  const [viewMode, setViewMode] = React.useState<'list' | 'grid'>('grid');
  const [gridScale, setGridScale] = React.useState(360);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [lifecycleStatus, setLifecycleStatus] = React.useState<Record<string, LifecycleStatus>>({});

  // Invite Owners modal state
  const [showInviteModal, setShowInviteModal] = React.useState(false);
  const [inviteModelId, setInviteModelId] = React.useState<string | null>(null);
  const [inviteData, setInviteData] = React.useState<any>(null);
  const [inviteLoading, setInviteLoading] = React.useState(false);
  const [inviteCreating, setInviteCreating] = React.useState(false);
  const [inviteEmailTemplate, setInviteEmailTemplate] = React.useState('');
  const [inviteCopiedIndex, setInviteCopiedIndex] = React.useState<number | null>(null);
  const [inviteCopiedAll, setInviteCopiedAll] = React.useState(false);
  const [executionSearch, setExecutionSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<'all' | 'Created' | 'Scheduled' | 'In-Process' | 'Completed'>('all');
  const [appSearch, setAppSearch] = React.useState('');
  const [appSearchModelIds, setAppSearchModelIds] = React.useState<Set<string> | null>(null);
  const [appSearchLoading, setAppSearchLoading] = React.useState(false);

  // Load lifecycle status for all APR models
  const loadLifecycleStatus = async () => {
    try {
      const response = await authFetch('/api/assessments/lifecycle-status');
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const statusMap: Record<string, LifecycleStatus> = {};
          data.data.forEach((item: LifecycleStatus) => {
            statusMap[item.apr_model_id] = item;
          });
          setLifecycleStatus(statusMap);
        }
      }
    } catch (error) {
      console.error('Error loading lifecycle status:', error);
    }
  };

  // Load lifecycle status on mount and when models change
  React.useEffect(() => {
    loadLifecycleStatus();
  }, [state.models]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshData();
      // Clear cached model applications to force reload
      setModelApplications({});
      // Refresh lifecycle status
      await loadLifecycleStatus();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Application search with debounce
  React.useEffect(() => {
    if (!appSearch.trim()) {
      setAppSearchModelIds(null);
      return;
    }
    const timer = setTimeout(async () => {
      setAppSearchLoading(true);
      try {
        const matchingModelIds = await apiService.searchModelsByApplication(appSearch.trim());
        setAppSearchModelIds(new Set(matchingModelIds));
      } catch {
        setAppSearchModelIds(new Set());
      }
      setAppSearchLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [appSearch]);

  // Filter to show Created and Scheduled APR models and In-Process/Completed models and runs
  const searchFilter = (model: APRModel) => {
    if (executionSearch.trim()) {
      const s = executionSearch.toLowerCase();
      if (!(model.name || '').toLowerCase().includes(s) && !(model.description || '').toLowerCase().includes(s)) return false;
    }
    if (appSearchModelIds !== null && !appSearchModelIds.has(model.id)) return false;
    return true;
  };
  const statusMatch = (model: APRModel, ...statuses: string[]) => {
    if (statusFilter !== 'all' && !statuses.includes(statusFilter)) return false;
    return statuses.includes(model.status);
  };
  const scheduledModels = state.models.filter(model => statusMatch(model, 'Created', 'Scheduled') && searchFilter(model));
  const inProcessModels = state.models.filter(model => statusMatch(model, 'In-Process') && searchFilter(model));
  const completedModels = state.models.filter(model => statusMatch(model, 'Completed') && searchFilter(model));
  const activeRuns = state.aprRuns.filter(run => 
    run.status === 'In-Process' || run.status === 'Completed'
  );
  
  // Combine all active items for display
  const allActiveItems = [...inProcessModels, ...completedModels, ...activeRuns];

  const getSelectedApplicationsForRun = (run: APRRun) => {
    if (!run.selectedApplicationIds || run.selectedApplicationIds.length === 0) {
      return [];
    }
    return state.applications.filter(app => 
      run.selectedApplicationIds!.includes(app.id)
    );
  };

  const getApplicationsForItem = async (item: APRRun | APRModel): Promise<Application[]> => {
    if ('rationalizationName' in item) {
      // It's an APR run
      return getSelectedApplicationsForRun(item);
    } else {
      // It's an APR model - check if we have cached applications
      if (modelApplications[item.id]) {
        return modelApplications[item.id];
      }
      
      // Fetch applications for this model
      try {
        setLoadingApplications(true);
        const applications = await apiService.getModelApplications(item.id);
        
        // Cache the results
        setModelApplications(prev => ({ ...prev, [item.id]: applications }));
        return applications;
      } catch (error) {
        console.error('Error fetching model applications:', error);
        return [];
      } finally {
        setLoadingApplications(false);
      }
    }
  };


  const handleViewDetails = async (item: APRRun | APRModel) => {
    setSelectedItem(item);
    
    // Pre-load applications for models
    if (!('rationalizationName' in item)) {
      await getApplicationsForItem(item);
    }
    
    setShowDetailsModal(true);
  };

  const handleModelDelete = async (item: APRRun | APRModel) => {
    if (!hasPermission('canManageUsers')) {
      alert('Access denied. Only Administrators can delete APR models.');
      return;
    }

    const name = 'rationalizationName' in item ? item.rationalizationName : item.name;
    const status = item.status;

    // Create status-specific warning messages
    let statusWarning = '';
    if (status === 'In-Process') {
      statusWarning = `\n⚠️  CRITICAL WARNING: This APR model is currently "In-Process" - users may be actively working on application scoring and their work will be LOST!\n`;
    } else if (status === 'Scheduled') {
      statusWarning = `\n⚠️  WARNING: This APR model is scheduled for execution and will be removed from the execution queue.\n`;
    } else if (status === 'Completed') {
      statusWarning = `\n⚠️  WARNING: This APR model is completed and contains valuable analysis results that will be permanently lost.\n`;
    }

    const confirmMessage = `Are you sure you want to PERMANENTLY DELETE the APR model "${name}"?${statusWarning}\nThis action cannot be undone and will:\n- Remove the model completely\n- Delete all associated data and user work\n- Cannot be recovered\n\nAs an Administrator, you can delete APR models at any status level.\n\nType "DELETE" to confirm this permanent action.`;
    
    const userConfirmation = prompt(confirmMessage);
    
    if (userConfirmation === 'DELETE') {
      try {
        if ('rationalizationName' in item) {
          // It's an APR run
          dispatch({ type: 'DELETE_APR_RUN', payload: item.id });
        } else {
          // It's an APR model
          await apiService.deleteModel(item.id);
          dispatch({ type: 'DELETE_MODEL', payload: item.id });
        }
        alert(`✅ APR model "${name}" (${status}) has been permanently deleted by Administrator.`);
      } catch (error) {
        alert(`❌ Error deleting APR model: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } else if (userConfirmation !== null) {
      alert('❌ Deletion cancelled. You must type "DELETE" exactly to confirm.');
    }
  };

  const handleExecuteAPR = async (item: APRRun | APRModel) => {
    const name = 'rationalizationName' in item ? item.rationalizationName : item.name;
    
    // Confirm the execution with the user
    if (window.confirm(`Are you sure you want to start Q&A for "${name}"? This will change the status to "In-Process" and make the applications available for questionnaire completion.`)) {
      try {
        // Show loading state
        setExecutingId(item.id);

        // Get applications for this item
        const applications = await getApplicationsForItem(item);
        
        if (applications.length === 0) {
          alert(`❌ Cannot start Q&A: No applications found for "${name}". Please add applications to this model first.`);
          return;
        }

        // Initialize Q&A assessments table for all applications and questions
        const initResult = await apiService.initializeQAAssessments(
          applications,
          'rationalizationName' in item ? item.id : undefined, // apr_run_id
          'rationalizationName' in item ? undefined : item.id   // apr_model_id
        );

        console.log(`✅ Q&A Assessments initialized:`, initResult);

        if ('rationalizationName' in item) {
          // It's an APR run
          const updatedRun: APRRun = {
            ...item,
            status: 'In-Process'
          };
          dispatch({
            type: 'UPDATE_APR_RUN',
            payload: updatedRun
          });
        } else {
          // It's an APR model - update it to In-Process
          const updatedModel = {
            ...item,
            status: 'In-Process' as const
          };
          await apiService.updateModel(item.id, updatedModel);
          dispatch({
            type: 'UPDATE_MODEL',
            payload: updatedModel
          });
        }

        // Show success message with details
        const frameworkInfo = initResult.framework_name
          ? `📚 Framework: ${initResult.framework_name}\n`
          : '';
        alert(`✅ Q&A started successfully for "${name}"\n\n` +
              `${frameworkInfo}` +
              `📊 ${initResult.applications_count} applications\n` +
              `❓ ${initResult.questions_count} questions each\n` +
              `📝 ${initResult.records_created} assessment records created\n\n` +
              `Select an application below to begin Q&A assessment.`);
        
      } catch (error) {
        alert(`❌ Error starting Q&A: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        // Clear loading state
        setExecutingId(null);
      }
    }
  };

  const handleStartQAForApplication = async (applicationId: string, applicationName: string, aprRunId?: string) => {
    setSelectedApplication({
      id: applicationId,
      name: applicationName,
      aprRunId
    });
    setShowQAModal(true);
  };

  const handleQAComplete = (results: AssessmentResults) => {
    console.log('Q&A Assessment completed:', results);
    setShowQAModal(false);
    setSelectedApplication(null);
    
    // Show completion message
    alert(`🎉 Assessment completed for ${selectedApplication?.name}!\n\n` +
          `📊 Overall Score: ${results.overallScore.toFixed(2)}/5\n` +
          `✅ Questions Answered: ${results.answeredQuestions}/${results.totalQuestions}\n` +
          `🎯 Recommendation: ${results.recommendation}\n\n` +
          `Great job! The assessment has been saved.`);
  };

  const handleQAModalClose = () => {
    setShowQAModal(false);
    setSelectedApplication(null);
  };

  const handleSchedule = async (model: APRModel) => {
    // Check if model is in the correct status for scheduling
    if (model.status !== 'Created') {
      alert(`❌ Cannot schedule "${model.name}". Only models with "Created" status can be scheduled. Current status: ${model.status}.`);
      return;
    }

    // Check if model has applications assigned
    if (!model.applicationCount || model.applicationCount === 0) {
      alert(`❌ Cannot schedule "${model.name}". The model must have at least 1 application assigned. Please go to Application Master Data to assign applications to this model first.`);
      return;
    }

    // Check that all applications have owner emails
    try {
      const modelApps = await apiService.getModelApplications(model.id);
      const missingEmails: string[] = [];
      for (const app of modelApps) {
        if (!app.appOwnerEmail || !app.appOwnerEmail.trim()) {
          missingEmails.push(`${app.applicationIdCode || app.id} — ${app.name}`);
        }
      }
      if (missingEmails.length > 0) {
        alert(
          `❌ Cannot schedule "${model.name}".\n\n` +
          `${missingEmails.length} application(s) are missing Application Owner Email.\n` +
          `Please update these in Application Master Data before scheduling:\n\n` +
          missingEmails.slice(0, 15).join('\n') +
          (missingEmails.length > 15 ? `\n...and ${missingEmails.length - 15} more` : '')
        );
        return;
      }
    } catch (error) {
      console.error('Error checking application emails:', error);
      alert('❌ Error validating application owner emails. Please try again.');
      return;
    }

    if (window.confirm(`Are you sure you want to schedule "${model.name}"?\n\nThis will:\n- Change the status to "Scheduled"\n- Make it available for execution\n- Lock the model configuration\n\nApplications assigned: ${model.applicationCount}`)) {
      try {
        // Update the model status from "Created" to "Scheduled"
        const updatedModel = {
          ...model,
          status: 'Scheduled' as const
        };
        
        // Update the model status via API
        await apiService.updateModel(model.id, updatedModel);
        
        // Update the model in context
        dispatch({
          type: 'UPDATE_MODEL',
          payload: updatedModel
        });

        alert(`✅ APR model "${model.name}" has been successfully scheduled!\n\n📅 Status: Created → Scheduled\n📱 Applications: ${model.applicationCount}\n\nThe model is now ready for execution.`);
        
      } catch (error) {
        alert(`❌ Error scheduling APR model: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  const handleUnschedule = async (model: APRModel) => {
    // Check if model is in the correct status for unscheduling
    if (model.status !== 'Scheduled') {
      if (model.status === 'In-Process') {
        alert(`❌ Cannot unschedule "${model.name}". The model has reached "In-Process" status and revert is not possible at this stage. Q&A is already in progress.`);
      } else {
        alert(`❌ Cannot unschedule "${model.name}". Only models with "Scheduled" status can be unscheduled. Current status: ${model.status}.`);
      }
      return;
    }

    // Ask user if they want to keep applications or remove them
    const keepApplications = window.confirm(`Unscheduling "${model.name}" will change the status back to "Created" and remove it from the execution queue.\n\nDo you want to KEEP the assigned applications (${model.applicationCount || 0} apps) for future reconfiguration?\n\nClick "OK" to KEEP applications\nClick "Cancel" to REMOVE applications`);
    
    const actionDescription = keepApplications 
      ? `This will:\n- Change the status back to "Created"\n- Remove it from the execution queue\n- Keep all assigned applications (${model.applicationCount || 0} apps)`
      : `This will:\n- Change the status back to "Created"\n- Remove it from the execution queue\n- Remove all assigned applications\n- Make applications available for other models`;
    
    if (window.confirm(`Are you sure you want to unschedule "${model.name}"?\n\n${actionDescription}`)) {
      try {
        // Update the model status from "Scheduled" to "Created"
        const updatedModel = {
          ...model,
          status: 'Created' as const,
          applicationCount: keepApplications ? model.applicationCount : 0
        };
        
        if (!keepApplications) {
          // Remove all applications from this model by updating with empty array
          await apiService.updateModelApplications(model.id, []);
        }
        
        // Update the model status
        await apiService.updateModel(model.id, updatedModel);
        
        // Update the model in context
        dispatch({
          type: 'UPDATE_MODEL',
          payload: updatedModel
        });
        
        if (!keepApplications) {
          // Update applications in context to remove aprModelId
          const applicationsToUpdate = state.applications.filter(app => app.aprModelId === model.id);
          applicationsToUpdate.forEach(app => {
            dispatch({
              type: 'UPDATE_APPLICATION',
              payload: { ...app, aprModelId: '' }
            });
          });
        }

        // Show success message
        const successMessage = keepApplications 
          ? `✅ APR model "${model.name}" has been unscheduled and moved back to "Created" status. All ${model.applicationCount || 0} assigned applications have been preserved.`
          : `✅ APR model "${model.name}" has been unscheduled and moved back to "Created" status. All applications have been removed and are now available for selection.`;
        
        alert(successMessage);
        
      } catch (error) {
        alert(`❌ Error unscheduling APR model: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  // Invite Application Owners handlers
  const getAppUrl = () => {
    return window.location.origin;
  };

  const getDefaultEmailTemplate = (_modelName: string) => {
    return `Dear Sir/Madam,

You have been designated as an Application Owner as part of the APR (Application Portfolio Rationalization) assessment.

Your email address has been registered and is now active in the APR Framework system. Your User ID is your email address.

Sign-in URL: ${getAppUrl()}/login
Your temporary password: Test!12345

IMPORTANT: You will be required to change your password upon first login.

NOTE: If you have already created your account, there is no need to re-register. Simply use your existing User ID (email address) and current password to sign in.

Once you log in, you will be automatically directed to the "Application Portfolio Q&A" screen. There you will see the application(s) for which you have been designated as an owner. Applications may be grouped under different APR models.

To complete your assessment:
1. Click the "Start Q&A" button next to your application
2. Answer each question one after the other
3. Once you have answered ALL questions, click "Save and Close"

If you are unable to complete all questions in one session:
- Click "Save" to preserve your progress at any point
- When you return, you will see a "Continue" button next to your application
- Click "Continue" to resume your Q&A session from where you left off
- Complete the remaining questions and then click "Save and Close"

If you have any questions or need assistance, please contact your APR administrator.

Thank you for your participation.

Best regards,
APR Framework Administration`;
  };

  const handleInviteOwners = async (model: APRModel) => {
    setInviteModelId(model.id);
    setInviteLoading(true);
    setShowInviteModal(true);
    setInviteCopiedIndex(null);
    setInviteCopiedAll(false);
    try {
      const data = await apiService.getModelOwners(model.id, false);
      setInviteData(data);
      setInviteEmailTemplate(getDefaultEmailTemplate(data.modelName || model.name));
    } catch (error) {
      console.error('Error loading owners:', error);
      alert('Failed to load application owners.');
      setShowInviteModal(false);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCreateOwnerAccounts = async () => {
    if (!inviteModelId) return;
    setInviteCreating(true);
    try {
      const data = await apiService.getModelOwners(inviteModelId, true);
      setInviteData(data);
      if (data.accountsCreated?.length > 0) {
        alert(`User accounts created for ${data.accountsCreated.length} application owner(s).\n\nPassword: Test!12345 (must change on first login)`);
      }
      if (data.accountsSkipped?.length > 0 && data.accountsCreated?.length === 0) {
        alert('All application owners already have user accounts.');
      }
    } catch (error) {
      console.error('Error creating accounts:', error);
      alert('Failed to create user accounts.');
    } finally {
      setInviteCreating(false);
    }
  };

  const copyToClipboard = (text: string): boolean => {
    // navigator.clipboard only works on HTTPS or localhost
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback for HTTP (e.g. AWS Elastic Beanstalk without HTTPS)
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      return true;
    } catch {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  };

  // Copy just the email addresses (semicolon-separated for Outlook)
  const handleCopyAllEmails = () => {
    if (!inviteData?.owners) return;
    const addresses = inviteData.owners.map((owner: any) => owner.email).join('; ');
    copyToClipboard(addresses);
    setInviteCopiedAll(true);
    setTimeout(() => setInviteCopiedAll(false), 2000);
  };

  // Copy the email subject
  const [copiedSubject, setCopiedSubject] = React.useState(false);
  const emailSubject = 'APR Assessment – Action Required: Application Portfolio Q&A';
  const handleCopySubject = () => {
    copyToClipboard(emailSubject);
    setCopiedSubject(true);
    setTimeout(() => setCopiedSubject(false), 2000);
  };

  // Copy the email body text from the template
  const [copiedBody, setCopiedBody] = React.useState(false);
  const handleCopyEmailBody = () => {
    copyToClipboard(inviteEmailTemplate);
    setCopiedBody(true);
    setTimeout(() => setCopiedBody(false), 2000);
  };

  // Copy individual owner's email address
  const handleCopyEmail = (owner: any, index: number) => {
    copyToClipboard(owner.email);
    setInviteCopiedIndex(index);
    setTimeout(() => setInviteCopiedIndex(null), 2000);
  };

  const getStatusChip = (status: string) => {
    const chipStyles = {
      'Created': 'status-chip status-chip--created',
      'Scheduled': 'status-chip status-chip--scheduled',
      'In-Process': 'status-chip status-chip--in-process',
      'Completed': 'status-chip status-chip--completed'
    }[status] || 'status-chip';

    const icons = {
      'Created': '📝',
      'Scheduled': '📅',
      'In-Process': '⚡',
      'Completed': '✅'
    }[status] || '📝';

    return (
      <span className={chipStyles}>
        <span className="status-chip__icon">{icons}</span>
        <span className="status-chip__text">{status}</span>
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Header - Fixed */}
      <div className="page-header" style={{ flexShrink: 0, position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#fff' }}>
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title" style={{ fontSize: '28px' }}>APR Model Execution</h1>
            <p className="page-header__subtitle" style={{ fontSize: '16px' }}>
              Execute scheduled APR models and track their progress
            </p>
          </div>
          <div className="page-header__actions" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              style={{
                padding: '8px 16px',
                backgroundColor: isRefreshing ? '#ccc' : '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isRefreshing ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              title="Refresh scoring status data"
            >
              <span style={{
                display: 'inline-block',
                animation: isRefreshing ? 'spin 1s linear infinite' : 'none'
              }}>🔄</span>
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <input
              type="text"
              placeholder="Search models..."
              value={executionSearch}
              onChange={(e) => setExecutionSearch(e.target.value)}
              style={{
                padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px',
                fontSize: '14px', minWidth: '200px', outline: 'none'
              }}
            />
            <input
              type="text"
              placeholder="Search by application ID or name..."
              value={appSearch}
              onChange={(e) => setAppSearch(e.target.value)}
              style={{
                padding: '8px 12px',
                border: appSearchModelIds !== null ? '2px solid #1976d2' : '1px solid #ddd',
                borderRadius: '6px', fontSize: '14px', minWidth: '260px', outline: 'none'
              }}
            />
            {appSearchLoading && <span style={{ fontSize: '13px', color: '#999' }}>Searching...</span>}
            {appSearchModelIds !== null && !appSearchLoading && (
              <button
                onClick={() => { setAppSearch(''); setAppSearchModelIds(null); }}
                style={{
                  padding: '4px 10px', fontSize: '12px', border: '1px solid #ccc',
                  borderRadius: '4px', cursor: 'pointer', backgroundColor: '#f5f5f5'
                }}
              >
                Clear app filter
              </button>
            )}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              style={{
                padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px',
                fontSize: '14px', outline: 'none', cursor: 'pointer'
              }}
            >
              <option value="all">All Statuses</option>
              <option value="Created">Created</option>
              <option value="Scheduled">Scheduled</option>
              <option value="In-Process">In-Process</option>
              <option value="Completed">Completed</option>
            </select>

            {/* Spacer to push toggle to far right */}
            <div style={{ flex: 1 }} />

            {/* Grid Scale Slider - only visible in grid mode */}
            {viewMode === 'grid' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: '#666' }}>Size:</span>
                <input
                  type="range"
                  min="250"
                  max="500"
                  value={gridScale}
                  onChange={(e) => setGridScale(Number(e.target.value))}
                  style={{ width: '100px', cursor: 'pointer' }}
                  title={`Grid card width: ${gridScale}px`}
                />
              </div>
            )}

            {/* View Toggle Buttons */}
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                onClick={() => setViewMode('list')}
                style={{
                  padding: '8px 10px',
                  border: viewMode === 'list' ? '2px solid #1976d2' : '1px solid #ddd',
                  backgroundColor: viewMode === 'list' ? '#e3f2fd' : 'white',
                  color: viewMode === 'list' ? '#1976d2' : '#666',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                title="List View"
              >
                ☰
              </button>
              <button
                onClick={() => setViewMode('grid')}
                style={{
                  padding: '8px 10px',
                  border: viewMode === 'grid' ? '2px solid #1976d2' : '1px solid #ddd',
                  backgroundColor: viewMode === 'grid' ? '#e3f2fd' : 'white',
                  color: viewMode === 'grid' ? '#1976d2' : '#666',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                title="Grid View"
              >
                ⊞
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Material Design Cards Layout - Scrollable */}
      <div className="md-content" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>

        {/* Scheduled Models and Active Items Display */}
        {scheduledModels.length === 0 && allActiveItems.length === 0 ? (
          <div className="md-empty-state">
            <div className="md-empty-state__icon">▶️</div>
            <h3 className="md-empty-state__title">No APR Models Available for Execution</h3>
            <p className="md-empty-state__description">
              Create or schedule APR models from the APR Model Definition section to see them here. Models in "Created" or "Scheduled" status will appear here ready for execution, and active models will show their progress.
            </p>
          </div>
        ) : viewMode === 'list' ? (
          /* List View */
          <div style={{ padding: '20px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#333' }}>Model Name</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#333' }}>Status</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#333' }}>Description</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#333' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Scheduled Models in List View */}
                {scheduledModels.map(model => {
                  const statusColor = model.status === 'Created' ? '#2e7d32' : model.status === 'Scheduled' ? '#1565c0' : model.status === 'In-Process' ? '#ef6c00' : '#7b1fa2';
                  const statusBg = model.status === 'Created' ? '#e8f5e8' : model.status === 'Scheduled' ? '#e3f2fd' : model.status === 'In-Process' ? '#fff3e0' : '#f3e5f5';
                  return (
                    <tr key={model.id} style={{ borderBottom: '1px solid #e0e0e0' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '20px' }}>🎯</span>
                          <span style={{ fontWeight: '500', color: '#333' }}>{model.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500', backgroundColor: statusBg, color: statusColor }}>
                          {model.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#666', fontSize: '14px' }}>{model.description || '-'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleViewDetails(model)}
                            style={{ padding: '6px 12px', backgroundColor: '#e3f2fd', color: '#1976d2', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleExecuteAPR(model)}
                            disabled={executingId === model.id}
                            style={{ padding: '6px 12px', backgroundColor: executingId === model.id ? '#ccc' : '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: executingId === model.id ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '500' }}
                          >
                            {executingId === model.id ? 'Starting...' : 'Start'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {/* Active Items in List View */}
                {allActiveItems.map(item => {
                  const isModel = !('rationalizationName' in item);
                  const name = isModel ? (item as APRModel).name : (item as APRRun).rationalizationName;
                  const status = item.status;
                  const description = isModel ? (item as APRModel).description : '';
                  const statusColor = status === 'In-Process' ? '#ef6c00' : '#7b1fa2';
                  const statusBg = status === 'In-Process' ? '#fff3e0' : '#f3e5f5';
                  return (
                    <tr key={isModel ? (item as APRModel).id : (item as APRRun).id} style={{ borderBottom: '1px solid #e0e0e0' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '20px' }}>{status === 'In-Process' ? '⚡' : '✅'}</span>
                          <span style={{ fontWeight: '500', color: '#333' }}>{name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500', backgroundColor: statusBg, color: statusColor }}>
                          {status}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#666', fontSize: '14px' }}>{description || '-'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleViewDetails(item)}
                            style={{ padding: '6px 12px', backgroundColor: '#e3f2fd', color: '#1976d2', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}
                          >
                            View Details
                          </button>
                          {status === 'In-Process' && isModel && hasPermission('canManageUsers') && (
                            <button
                              onClick={() => handleInviteOwners(item as APRModel)}
                              style={{ padding: '6px 12px', backgroundColor: '#1565c0', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}
                            >
                              Invite Owners
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          /* Grid View */
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${gridScale}px, 1fr))`,
            gap: '20px',
            padding: '20px'
          }}>
            {/* Scheduled Models */}
            {scheduledModels.map(model => {
              const statusBorderColor = model.status === 'Created' ? '#2e7d32' : model.status === 'Scheduled' ? '#1565c0' : model.status === 'In-Process' ? '#ef6c00' : '#7b1fa2';
              return (
                <div
                  key={model.id}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    border: `2px solid ${statusBorderColor}`,
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    transition: 'box-shadow 0.2s, transform 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Header with Status */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', height: '52px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: '24px', flexShrink: 0 }}>🎯</span>
                      <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#333', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any }} title={model.name}>{model.name}</h3>
                    </div>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '2px',
                      padding: '6px 10px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '500',
                      flexShrink: 0,
                      backgroundColor: model.status === 'Created' ? '#e8f5e8' : model.status === 'Scheduled' ? '#e3f2fd' : model.status === 'In-Process' ? '#fff3e0' : '#f3e5f5',
                      color: model.status === 'Created' ? '#2e7d32' : model.status === 'Scheduled' ? '#1565c0' : model.status === 'In-Process' ? '#ef6c00' : '#7b1fa2'
                    }}>
                      <span style={{ fontSize: '16px' }}>
                        {model.status === 'Created' && '📝'}
                        {model.status === 'Scheduled' && '📅'}
                        {model.status === 'In-Process' && '⚡'}
                        {model.status === 'Completed' && '✅'}
                      </span>
                      <span>{model.status}</span>
                    </div>
                  </div>

                  {/* Description */}
                  <p style={{
                    margin: 0,
                    fontSize: '12px',
                    color: '#666',
                    lineHeight: '1.4',
                    height: '34px',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical' as any
                  }} title={model.description || ''}>
                    {model.description || 'No description provided'}
                  </p>

                  {/* Info Row */}
                  <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#666' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span>📚</span>
                      <span>{model.frameworkName || 'No framework'}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span>📱</span>
                      <span>{model.applicationCount || 0} apps</span>
                    </div>
                  </div>

                  {/* Lifecycle Progress Bars - 3 Separate Rows with Segmented Style */}
                  {(() => {
                    const modelLifecycle = lifecycleStatus[model.id];
                    const totalApps = modelLifecycle?.total_applications || model.applicationCount || 0;
                    const isInProcess = model.status === 'In-Process';

                    // Color gradient from red to dark green based on progress
                    const getProgressColor = (completedIndex: number, total: number) => {
                      if (total === 0) return '#bdbdbd';
                      const percent = ((completedIndex + 1) / total) * 100;
                      if (percent <= 25) return '#dc3545'; // Red
                      if (percent <= 50) return '#fd7e14'; // Orange
                      if (percent <= 75) return '#ffc107'; // Yellow
                      if (percent <= 90) return '#66bb6a'; // Light green
                      return '#2e7d32'; // Dark green
                    };

                    // Helper to render a segmented progress bar row
                    const renderSegmentedProgressBar = (
                      label: string,
                      icon: string,
                      completed: number,
                      total: number
                    ) => {
                      // Calculate number of segments (max 20 for display, scale if more)
                      const maxSegments = 20;
                      const displaySegments = Math.min(total, maxSegments);
                      const scaleFactor = total > maxSegments ? total / maxSegments : 1;
                      const filledSegments = Math.round(completed / scaleFactor);

                      return (
                        <div style={{ marginBottom: '10px' }}>
                          {/* Progress label */}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '11px',
                            color: isInProcess ? '#333' : '#999',
                            marginBottom: '4px'
                          }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span>{icon}</span>
                              <span>{label}</span>
                            </span>
                            <span style={{ fontWeight: '500' }}>{completed} / {total}</span>
                          </div>
                          {/* Segmented progress bar */}
                          <div style={{
                            display: 'flex',
                            gap: '2px',
                            width: '100%',
                            height: '12px'
                          }}>
                            {Array.from({ length: displaySegments || 1 }).map((_, index) => {
                              const isFilled = index < filledSegments;
                              const segmentColor = isFilled && isInProcess
                                ? getProgressColor(index, displaySegments)
                                : isFilled
                                  ? '#bdbdbd'
                                  : '#e8e8e8';
                              return (
                                <div
                                  key={index}
                                  style={{
                                    flex: 1,
                                    height: '100%',
                                    backgroundColor: segmentColor,
                                    borderRadius: '2px',
                                    transition: 'background-color 0.3s ease'
                                  }}
                                />
                              );
                            })}
                          </div>
                          {/* Scale markers */}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '10px',
                            color: isInProcess ? '#666' : '#999',
                            marginTop: '2px'
                          }}>
                            <span>0</span>
                            <span>{total}</span>
                          </div>
                        </div>
                      );
                    };

                    // Get lifecycle data or use fallback
                    const qaCompleted = modelLifecycle?.qa_assessment?.completed || model.completedApplications || 0;
                    const dispositionCompleted = modelLifecycle?.final_disposition?.completed || 0;
                    const integrationTransferred = (modelLifecycle?.integration_transfer?.transferred || 0) +
                                                   (modelLifecycle?.integration_transfer?.retain_confirmed || 0);

                    return (
                      <div style={{ width: '95%', margin: '8px auto' }}>
                        {/* Row 1: Q&A Assessment */}
                        {renderSegmentedProgressBar(
                          'Q&A Assessment',
                          '📝',
                          qaCompleted,
                          totalApps
                        )}

                        {/* Row 2: Final Disposition */}
                        {renderSegmentedProgressBar(
                          'Final Disposition',
                          '🎯',
                          dispositionCompleted,
                          totalApps
                        )}

                        {/* Row 3: Integration Transfer */}
                        {renderSegmentedProgressBar(
                          'Integration Transfer',
                          '🔄',
                          integrationTransferred,
                          totalApps
                        )}

                        {/* Row 4: In Development */}
                        {renderSegmentedProgressBar(
                          'In Development',
                          '🔧',
                          modelLifecycle?.in_development?.count || 0,
                          totalApps
                        )}

                        {/* Row 5: Disposed */}
                        {renderSegmentedProgressBar(
                          'Disposed',
                          '✅',
                          modelLifecycle?.disposed?.count || 0,
                          totalApps
                        )}
                      </div>
                    );
                  })()}

                  {/* Divider + Actions pinned to bottom */}
                  <div style={{ marginTop: 'auto' }}>
                    <div style={{ borderTop: '1px solid #eee', margin: '4px 0 8px 0' }} />
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                      {model.status === 'Created' ? (
                        <button
                          onClick={() => handleSchedule(model)}
                          title="Schedule model"
                          style={{
                            padding: '6px 10px',
                            backgroundColor: 'transparent',
                            color: '#1976d2',
                            border: '1px solid #1976d2',
                            borderRadius: '6px',
                            fontSize: '11px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <span>📅</span>
                          <span>Schedule</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleExecuteAPR(model)}
                          disabled={executingId === model.id}
                          title="Start Q&A"
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#2e7d32',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '11px',
                            cursor: executingId === model.id ? 'not-allowed' : 'pointer',
                            opacity: executingId === model.id ? 0.6 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <span>{executingId === model.id ? '⏳' : '▶️'}</span>
                          <span>{executingId === model.id ? 'Starting...' : 'Start Q&A'}</span>
                        </button>
                      )}
                      <button
                        onClick={() => handleViewDetails(model)}
                        title="View details"
                        style={{
                          padding: '6px 10px',
                          backgroundColor: 'transparent',
                          color: '#1976d2',
                          border: '1px solid #1976d2',
                          borderRadius: '6px',
                          fontSize: '11px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <span>👁️</span>
                        <span>View</span>
                      </button>
                      {model.status === 'Scheduled' && (
                        <button
                          onClick={() => handleUnschedule(model)}
                          title="Unschedule model"
                          style={{
                            padding: '6px 10px',
                            backgroundColor: 'transparent',
                            color: '#f57c00',
                            border: '1px solid #f57c00',
                            borderRadius: '6px',
                            fontSize: '11px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <span>↩️</span>
                          <span>Unschedule</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Active Items (Models and Runs) */}
            {allActiveItems.map(item => {
              const statusBorderColor = item.status === 'Created' ? '#2e7d32' : item.status === 'Scheduled' ? '#1565c0' : item.status === 'In-Process' ? '#ef6c00' : '#7b1fa2';
              const itemName = 'rationalizationName' in item ? item.rationalizationName : item.name;
              const itemDescription = 'description' in item ? item.description : '';
              const itemFrameworkName = 'frameworkName' in item ? item.frameworkName : null;
              return (
                <div
                  key={item.id}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    border: `2px solid ${statusBorderColor}`,
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    transition: 'box-shadow 0.2s, transform 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Header with Status */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', height: '52px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: '24px', flexShrink: 0 }}>🎯</span>
                      <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#333', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any }} title={itemName}>{itemName}</h3>
                    </div>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '2px',
                      padding: '6px 10px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '500',
                      flexShrink: 0,
                      backgroundColor: item.status === 'Created' ? '#e8f5e8' : item.status === 'Scheduled' ? '#e3f2fd' : item.status === 'In-Process' ? '#fff3e0' : '#f3e5f5',
                      color: item.status === 'Created' ? '#2e7d32' : item.status === 'Scheduled' ? '#1565c0' : item.status === 'In-Process' ? '#ef6c00' : '#7b1fa2'
                    }}>
                      <span style={{ fontSize: '16px' }}>
                        {item.status === 'Created' && '📝'}
                        {item.status === 'Scheduled' && '📅'}
                        {item.status === 'In-Process' && '⚡'}
                        {item.status === 'Completed' && '✅'}
                      </span>
                      <span>{item.status}</span>
                    </div>
                  </div>

                  {/* Description */}
                  <p style={{
                    margin: 0,
                    fontSize: '12px',
                    color: '#666',
                    lineHeight: '1.4',
                    height: '34px',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical' as any
                  }} title={itemDescription || ''}>
                    {itemDescription || 'No description provided'}
                  </p>

                  {/* Info Row */}
                  <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#666' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span>📚</span>
                      <span>{itemFrameworkName || 'No framework'}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span>📱</span>
                      <span>{item.applicationCount || 0} apps</span>
                    </div>
                  </div>

                  {/* Created/Completed dates for APR Runs */}
                  {'createdDate' in item && (
                    <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#888' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span>📆</span>
                        <span>Created: {formatDate(item.createdDate)}</span>
                      </div>
                      {item.completedDate && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span>✓</span>
                          <span>Completed: {formatDate(item.completedDate)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Lifecycle Progress Bars - 3 Separate Rows with Segmented Style */}
                  {(() => {
                    const modelLifecycle = lifecycleStatus[item.id];
                    const totalApps = modelLifecycle?.total_applications || item.applicationCount || 0;
                    const isInProcess = item.status === 'In-Process';

                    // Color gradient from red to dark green based on progress
                    const getProgressColor = (completedIndex: number, total: number) => {
                      if (total === 0) return '#bdbdbd';
                      const percent = ((completedIndex + 1) / total) * 100;
                      if (percent <= 25) return '#dc3545'; // Red
                      if (percent <= 50) return '#fd7e14'; // Orange
                      if (percent <= 75) return '#ffc107'; // Yellow
                      if (percent <= 90) return '#66bb6a'; // Light green
                      return '#2e7d32'; // Dark green
                    };

                    // Helper to render a segmented progress bar row
                    const renderSegmentedProgressBar = (
                      label: string,
                      icon: string,
                      completed: number,
                      total: number
                    ) => {
                      // Calculate number of segments (max 20 for display, scale if more)
                      const maxSegments = 20;
                      const displaySegments = Math.min(total, maxSegments);
                      const scaleFactor = total > maxSegments ? total / maxSegments : 1;
                      const filledSegments = Math.round(completed / scaleFactor);

                      return (
                        <div style={{ marginBottom: '10px' }}>
                          {/* Progress label */}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '11px',
                            color: isInProcess ? '#333' : '#999',
                            marginBottom: '4px'
                          }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span>{icon}</span>
                              <span>{label}</span>
                            </span>
                            <span style={{ fontWeight: '500' }}>{completed} / {total}</span>
                          </div>
                          {/* Segmented progress bar */}
                          <div style={{
                            display: 'flex',
                            gap: '2px',
                            width: '100%',
                            height: '12px'
                          }}>
                            {Array.from({ length: displaySegments || 1 }).map((_, index) => {
                              const isFilled = index < filledSegments;
                              const segmentColor = isFilled && isInProcess
                                ? getProgressColor(index, displaySegments)
                                : isFilled
                                  ? '#bdbdbd'
                                  : '#e8e8e8';
                              return (
                                <div
                                  key={index}
                                  style={{
                                    flex: 1,
                                    height: '100%',
                                    backgroundColor: segmentColor,
                                    borderRadius: '2px',
                                    transition: 'background-color 0.3s ease'
                                  }}
                                />
                              );
                            })}
                          </div>
                          {/* Scale markers */}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '10px',
                            color: isInProcess ? '#666' : '#999',
                            marginTop: '2px'
                          }}>
                            <span>0</span>
                            <span>{total}</span>
                          </div>
                        </div>
                      );
                    };

                    // Get lifecycle data or use fallback
                    const qaCompleted = modelLifecycle?.qa_assessment?.completed || (item as any).completedApplications || 0;
                    const dispositionCompleted = modelLifecycle?.final_disposition?.completed || 0;
                    const integrationTransferred = (modelLifecycle?.integration_transfer?.transferred || 0) +
                                                   (modelLifecycle?.integration_transfer?.retain_confirmed || 0);

                    return (
                      <div style={{ width: '95%', margin: '8px auto' }}>
                        {/* Row 1: Q&A Assessment */}
                        {renderSegmentedProgressBar(
                          'Q&A Assessment',
                          '📝',
                          qaCompleted,
                          totalApps
                        )}

                        {/* Row 2: Final Disposition */}
                        {renderSegmentedProgressBar(
                          'Final Disposition',
                          '🎯',
                          dispositionCompleted,
                          totalApps
                        )}

                        {/* Row 3: Integration Transfer */}
                        {renderSegmentedProgressBar(
                          'Integration Transfer',
                          '🔄',
                          integrationTransferred,
                          totalApps
                        )}

                        {/* Row 4: In Development */}
                        {renderSegmentedProgressBar(
                          'In Development',
                          '🔧',
                          modelLifecycle?.in_development?.count || 0,
                          totalApps
                        )}

                        {/* Row 5: Disposed */}
                        {renderSegmentedProgressBar(
                          'Disposed',
                          '✅',
                          modelLifecycle?.disposed?.count || 0,
                          totalApps
                        )}
                      </div>
                    );
                  })()}

                  {/* Divider + Actions pinned to bottom */}
                  <div style={{ marginTop: 'auto' }}>
                    <div style={{ borderTop: '1px solid #eee', margin: '4px 0 8px 0' }} />
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                      {item.status === 'Scheduled' && (
                        <button
                          onClick={() => handleExecuteAPR(item)}
                          disabled={executingId === item.id}
                          title="Start Q&A"
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#2e7d32',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '11px',
                            cursor: executingId === item.id ? 'not-allowed' : 'pointer',
                            opacity: executingId === item.id ? 0.6 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <span>{executingId === item.id ? '⏳' : '▶️'}</span>
                          <span>{executingId === item.id ? 'Starting...' : 'Start Q&A'}</span>
                        </button>
                      )}
                      {item.status === 'In-Process' && !('rationalizationName' in item) && hasPermission('canManageUsers') && (
                        <button
                          onClick={() => handleInviteOwners(item as APRModel)}
                          title="Invite application owners to score"
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#1565c0',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '11px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <span>📧</span>
                          <span>Invite</span>
                        </button>
                      )}
                      <button
                        onClick={() => handleViewDetails(item)}
                        title="View details"
                        style={{
                          padding: '6px 10px',
                          backgroundColor: 'transparent',
                          color: '#1976d2',
                          border: '1px solid #1976d2',
                          borderRadius: '6px',
                          fontSize: '11px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <span>👁️</span>
                        <span>View</span>
                      </button>
                      {hasPermission('canManageUsers') && (
                        <button
                          onClick={() => handleModelDelete(item)}
                          title="Administrator only - Permanent model deletion"
                          style={{
                            padding: '6px 10px',
                            backgroundColor: 'transparent',
                            color: '#d32f2f',
                            border: '1px solid #d32f2f',
                            borderRadius: '6px',
                            fontSize: '11px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <span>🗑️</span>
                          <span>Delete</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* View Details Modal */}
      {showDetailsModal && selectedItem && (
        <div className="md-scrim" onClick={(e) => e.target === e.currentTarget && setShowDetailsModal(false)}>
          <div className="md-dialog" style={{ maxWidth: '1000px' }}>
            <div className="md-dialog__header">
              <h2 className="md-dialog__title">
                {'rationalizationName' in selectedItem 
                  ? `APR Run Details: "${selectedItem.rationalizationName}"` 
                  : `APR Model Details: "${selectedItem.name}"`}
              </h2>
              <button 
                className="md-icon-button"
                onClick={() => setShowDetailsModal(false)}
                aria-label="Close dialog"
              >
                ✕
              </button>
            </div>
            
            <div className="md-dialog__content" style={{ maxHeight: '600px', overflowY: 'auto' }}>
              <div className="apr-run-summary">
                <div className="apr-run-summary__item">
                  <span className="apr-run-summary__label">Status:</span>
                  <span className="apr-run-summary__value">{getStatusChip(selectedItem.status)}</span>
                </div>
                <div className="apr-run-summary__item">
                  <span className="apr-run-summary__label">Description:</span>
                  <span className="apr-run-summary__value">{'description' in selectedItem ? selectedItem.description : ''}</span>
                </div>
                {'createdDate' in selectedItem && (
                  <div className="apr-run-summary__item">
                    <span className="apr-run-summary__label">Created:</span>
                    <span className="apr-run-summary__value">{formatDate(selectedItem.createdDate)}</span>
                  </div>
                )}
                {'createdDate' in selectedItem && selectedItem.completedDate && (
                  <div className="apr-run-summary__item">
                    <span className="apr-run-summary__label">Completed:</span>
                    <span className="apr-run-summary__value">{formatDate(selectedItem.completedDate)}</span>
                  </div>
                )}
              </div>

              <h3 className="applications-section-title">Applications in Scope ({selectedItem.applicationCount})</h3>
              
              {(() => {
                const isRun = 'rationalizationName' in selectedItem;
                const applications = isRun 
                  ? getSelectedApplicationsForRun(selectedItem)
                  : modelApplications[selectedItem.id] || [];
                
                if (loadingApplications && !isRun) {
                  return (
                    <div className="md-empty-state">
                      <div className="md-empty-state__icon">⏳</div>
                      <h3 className="md-empty-state__title">Loading Applications...</h3>
                      <p className="md-empty-state__description">
                        Fetching applications for this APR model.
                      </p>
                    </div>
                  );
                }
                
                if (applications.length === 0) {
                  return (
                    <div className="md-empty-state">
                      <div className="md-empty-state__icon">📱</div>
                      <h3 className="md-empty-state__title">No Applications Configured</h3>
                      <p className="md-empty-state__description">
                        {isRun ? 'This APR run has no applications configured.' : 'This APR model has no applications assigned.'}
                      </p>
                    </div>
                  );
                }
                
                return (
                  <div className="execution-details-table">
                    <div className="execution-details-table__header">
                      <div className="execution-details-table__cell execution-details-table__cell--header">Application</div>
                      <div className="execution-details-table__cell execution-details-table__cell--header">Application Owner</div>
                      <div className="execution-details-table__cell execution-details-table__cell--header">Application Owner Email</div>
                      <div className="execution-details-table__cell execution-details-table__cell--header">Technology</div>
                      <div className="execution-details-table__cell execution-details-table__cell--header">Users</div>
                      <div className="execution-details-table__cell execution-details-table__cell--header">Business Domain</div>
                      <div className="execution-details-table__cell execution-details-table__cell--header">Q&A Actions</div>
                    </div>
                    
                    {applications.map(app => (
                      <div key={app.id} className="execution-details-table__row">
                        <div className="execution-details-table__cell">
                          <div className="app-detail-info">
                            <div className="app-detail-info__name">{app.name}</div>
                            {app.applicationIdCode && (
                              <div className="app-detail-info__id">ID: {app.applicationIdCode}</div>
                            )}
                          </div>
                        </div>
                        <div className="execution-details-table__cell">
                          <div className="owner-info">
                            <div className="owner-info__name">
                              {app.applicationOwner || 'Not specified'}
                            </div>
                            {app.technicalOwnerCustodian && app.technicalOwnerCustodian !== app.applicationOwner && (
                              <div className="owner-info__technical">
                                Tech Owner: {app.technicalOwnerCustodian}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="execution-details-table__cell">
                          <div className="email-info">
                            {app.appOwnerEmail ? (
                              <a href={`mailto:${app.appOwnerEmail}`} className="email-link">
                                {app.appOwnerEmail}
                              </a>
                            ) : (
                              <span className="email-info--none">Not specified</span>
                            )}
                          </div>
                        </div>
                        <div className="execution-details-table__cell">
                          {app.technologyStack || 'Not specified'}
                        </div>
                        <div className="execution-details-table__cell">
                          {app.userCount?.toLocaleString() || 'Unknown'}
                        </div>
                        <div className="execution-details-table__cell">
                          {app.businessFunctionDomain || 'Not specified'}
                        </div>
                        <div className="execution-details-table__cell">
                          <button
                            type="button"
                            className="md-button md-button--outlined md-button--small"
                            onClick={() => handleStartQAForApplication(
                              app.id, 
                              app.name, 
                              'rationalizationName' in selectedItem ? selectedItem.id : undefined
                            )}
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                          >
                            🎯 Start Q&A
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            <div className="md-dialog__actions">
              {selectedItem.status === 'Scheduled' && (
                <button 
                  type="button" 
                  className="md-button md-button--filled"
                  onClick={() => {
                    handleExecuteAPR(selectedItem);
                    setShowDetailsModal(false);
                  }}
                  disabled={executingId === selectedItem.id}
                >
                  {executingId === selectedItem.id ? (
                    <>
                      <span className="md-button__spinner">⏳</span>
                      Executing...
                    </>
                  ) : (
                    'Execute APR'
                  )}
                </button>
              )}
              {hasPermission('canManageUsers') && (
                <button 
                  type="button" 
                  className="md-button md-button--text md-button--error"
                  onClick={() => {
                    handleModelDelete(selectedItem);
                    setShowDetailsModal(false);
                  }}
                  title="Administrator only - Permanent model deletion"
                >
                  Model Delete
                </button>
              )}
              <button 
                type="button" 
                className="md-button md-button--text" 
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Application Owners Modal */}
      {showInviteModal && (
        <div className="md-scrim" onClick={(e) => e.target === e.currentTarget && setShowInviteModal(false)}>
          <div className="md-dialog" style={{ maxWidth: '900px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="md-dialog__header">
              <h2 className="md-dialog__title">
                Invite Application Owners {inviteData?.modelName ? `- "${inviteData.modelName}"` : ''}
              </h2>
              <button
                className="md-icon-button"
                onClick={() => setShowInviteModal(false)}
                aria-label="Close dialog"
              >
                ✕
              </button>
            </div>

            <div className="md-dialog__content" style={{ flex: 1, overflowY: 'auto' }}>
              {inviteLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>Loading...</div>
                  <p>Fetching application owners...</p>
                </div>
              ) : inviteData ? (
                <>
                  {/* Summary */}
                  <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
                    <div style={{ padding: '12px 20px', backgroundColor: '#e3f2fd', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#1565c0' }}>{inviteData.totalApplications}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Total Applications</div>
                    </div>
                    <div style={{ padding: '12px 20px', backgroundColor: '#e8f5e9', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#2e7d32' }}>{inviteData.owners?.length || 0}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Unique Owners</div>
                    </div>
                    <div style={{ padding: '12px 20px', backgroundColor: '#fff3e0', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#ef6c00' }}>
                        {inviteData.owners?.filter((o: any) => !o.existingUser).length || 0}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Need Accounts</div>
                    </div>
                    <div style={{ padding: '12px 20px', backgroundColor: '#f3e5f5', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#7b1fa2' }}>
                        {inviteData.owners?.filter((o: any) => o.existingUser).length || 0}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Have Accounts</div>
                      {inviteData.owners?.some((o: any) => o.existingUser && o.existingUser.role !== 'Application Owner') && (
                        <div style={{ fontSize: '10px', color: '#7b1fa2', marginTop: '2px' }}>
                          (incl. {inviteData.owners.filter((o: any) => o.existingUser && o.existingUser.role !== 'Application Owner').length} with other roles)
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Create Accounts Button */}
                  {inviteData.owners?.some((o: any) => !o.existingUser) && (
                    <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#fff8e1', borderRadius: '8px', border: '1px solid #ffe082' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <strong>Step 1:</strong> Create user accounts for owners who don't have one yet.
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            Accounts will be created with role "Application Owner" and temporary password "Test!12345"
                          </div>
                        </div>
                        <button
                          onClick={handleCreateOwnerAccounts}
                          disabled={inviteCreating}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: inviteCreating ? '#ccc' : '#2e7d32',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: inviteCreating ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {inviteCreating ? 'Creating...' : 'Create Accounts'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Email Template */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <label style={{ fontWeight: '600', fontSize: '14px' }}>
                        {inviteData.owners?.some((o: any) => !o.existingUser) ? 'Step 2: ' : ''}Email Template (editable)
                      </label>
                      <button
                        onClick={() => setInviteEmailTemplate(getDefaultEmailTemplate(inviteData.modelName))}
                        style={{
                          padding: '4px 12px',
                          backgroundColor: 'transparent',
                          color: '#1976d2',
                          border: '1px solid #1976d2',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Reset to Default
                      </button>
                    </div>
                    <textarea
                      value={inviteEmailTemplate}
                      onChange={(e) => setInviteEmailTemplate(e.target.value)}
                      style={{
                        width: '100%',
                        minHeight: '200px',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        lineHeight: '1.5',
                        resize: 'vertical',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  {/* Owners Table */}
                  {(() => {
                    const allHaveAccounts = inviteData.owners?.every((o: any) => o.existingUser);
                    const noAccountCount = inviteData.owners?.filter((o: any) => !o.existingUser).length || 0;
                    return (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: allHaveAccounts ? 0 : '8px' }}>
                      <h3 style={{ margin: 0, fontSize: '16px' }}>Application Owners ({inviteData.owners?.length || 0})</h3>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={handleCopyAllEmails}
                          disabled={!allHaveAccounts}
                          title={!allHaveAccounts ? `Create accounts first — ${noAccountCount} owner(s) without accounts` : 'Copy all email addresses to clipboard'}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: !allHaveAccounts ? '#ccc' : inviteCopiedAll ? '#2e7d32' : '#1565c0',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: !allHaveAccounts ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                            fontWeight: '500'
                          }}
                        >
                          {inviteCopiedAll ? 'Copied!' : 'Copy All Email Addresses'}
                        </button>
                        <button
                          onClick={handleCopySubject}
                          disabled={!allHaveAccounts}
                          title={!allHaveAccounts ? 'Create accounts first' : 'Copy email subject to clipboard'}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: !allHaveAccounts ? '#ccc' : copiedSubject ? '#2e7d32' : '#ef6c00',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: !allHaveAccounts ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                            fontWeight: '500'
                          }}
                        >
                          {copiedSubject ? 'Copied!' : 'Copy Subject'}
                        </button>
                        <button
                          onClick={handleCopyEmailBody}
                          disabled={!allHaveAccounts}
                          title={!allHaveAccounts ? `Create accounts first — ${noAccountCount} owner(s) without accounts` : 'Copy email body to clipboard'}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: !allHaveAccounts ? '#ccc' : copiedBody ? '#2e7d32' : '#6a1b9a',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: !allHaveAccounts ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                            fontWeight: '500'
                          }}
                        >
                          {copiedBody ? 'Copied!' : 'Copy Email Body'}
                        </button>
                      </div>
                    </div>
                    {!allHaveAccounts && (
                      <div style={{ padding: '8px 12px', backgroundColor: '#fff3e0', borderRadius: '6px', fontSize: '12px', color: '#e65100' }}>
                        ⚠️ {noAccountCount} owner(s) do not have accounts yet. Please click "Create Accounts" before copying emails.
                      </div>
                    )}
                  </div>
                    );
                  })()}

                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #e0e0e0' }}>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600' }}>Owner Name</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600' }}>Email</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600' }}>Application(s)</th>
                        <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '600' }}>Account</th>
                        <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '600' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inviteData.owners?.map((owner: any, index: number) => (
                        <tr key={owner.email} style={{ borderBottom: '1px solid #e0e0e0' }}>
                          <td style={{ padding: '10px 12px', fontWeight: '500' }}>{owner.name || '-'}</td>
                          <td style={{ padding: '10px 12px', color: '#1565c0' }}>{owner.email}</td>
                          <td style={{ padding: '10px 12px' }}>
                            {owner.applications.map((app: any) => (
                              <div key={app.id} style={{ fontSize: '12px', marginBottom: '2px' }}>
                                {app.name}{app.code ? ` (${app.code})` : ''}
                              </div>
                            ))}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            {owner.existingUser ? (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                <span style={{ padding: '3px 10px', backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '12px', fontSize: '11px', fontWeight: '500' }}>
                                  Active
                                </span>
                                <span style={{ fontSize: '10px', color: '#666' }}>
                                  {owner.existingUser.role}
                                </span>
                              </div>
                            ) : (
                              <span style={{ padding: '3px 10px', backgroundColor: '#fff3e0', color: '#ef6c00', borderRadius: '12px', fontSize: '11px', fontWeight: '500' }}>
                                No Account
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            <button
                              onClick={() => handleCopyEmail(owner, index)}
                              disabled={!owner.existingUser}
                              title={!owner.existingUser ? 'Create account first' : 'Copy email address'}
                              style={{
                                padding: '6px 14px',
                                backgroundColor: !owner.existingUser ? '#ccc' : inviteCopiedIndex === index ? '#2e7d32' : '#1976d2',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: !owner.existingUser ? 'not-allowed' : 'pointer',
                                fontSize: '12px',
                                fontWeight: '500'
                              }}
                            >
                              {inviteCopiedIndex === index ? 'Copied!' : 'Copy Address'}
                            </button>
                          </td>
                        </tr>
                      ))}
                      {(!inviteData.owners || inviteData.owners.length === 0) && (
                        <tr>
                          <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                            No application owners with email addresses found for this model.
                            Please ensure applications have owner emails set in Application Master Data.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </>
              ) : null}
            </div>

            <div className="md-dialog__actions">
              <button
                type="button"
                className="md-button md-button--text"
                onClick={() => setShowInviteModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Q&A Assessment Modal */}
      {showQAModal && selectedApplication && (
        <QAAssessmentModal
          applicationId={selectedApplication.id}
          applicationName={selectedApplication.name}
          aprRunId={selectedApplication.aprRunId}
          isOpen={showQAModal}
          onClose={handleQAModalClose}
          onComplete={handleQAComplete}
        />
      )}
    </div>
  );
};

export default APRExecution;